import React, { useEffect } from 'react';
import { useAiGenerate, useAudioTabs, useTextToSpeech } from '../../../hooks';
import { FormProvider, RHFUploadSingleFile, NextImage, Iconify } from '@sdf/base';
import { Styles } from '@t2c/shared-fe';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { useMediaQuery } from '@mui/system';
import TextField from '@mui/material/TextField';
import { useVoiceover } from '../../../../t2c-fe/hooks/text-to-speech/useVoiceover';
import { CreditDialog } from '../../create-video/CreditDialog';
import { VoiceoverProccessingModal } from '../../voiceover/VoiceoverProccessingModal';
import { Chip, CircularProgress, IconButton, Popover, Tab, Tooltip, useTheme } from '@mui/material';
import { Tabs } from '@mui/material';
import { AudioTab } from '../../../../t2c-fe/@types';
import { DbModelToDisplayName } from 'apps/t2c-fe/utils';


export const TextToAudioWorkBench = ({ refetchCreatedAudios, isPage }) => {
  const {
    handleDataChange,
    handleCreateClick,
    isCreditDialogOpen,
    closeCreditDialog,
    isFetchingRequiredCredits,
    requiredCreditsData,
    handleConfirmGeneration,
    isProccessingModalOpen,
    closeProccessingModal,
    handleUpgrade,
    voiceOverData,
    popoverAnchor,
    handleOpenPopover,
    handleClosePopover,
    handleAudioSelect,
    privateAudioData,
    publicAudioData,
    isLoading,
    newGenerationData,
    isEnhancing,
    handlePromptEnhance,
  } = useVoiceover({ refetchCreatedAudios: refetchCreatedAudios });

  const { activeTab, handleTabChange, audioList, selectedAudio, handleAudioTrackSelect } =
    useAudioTabs(publicAudioData, privateAudioData);

  useEffect(() => {
    const defaultAudio = publicAudioData?.[0] ?? privateAudioData?.pages?.[0]?.data?.[0];

    if (defaultAudio && !selectedAudio) {
      handleAudioTrackSelect(defaultAudio);
      handleAudioSelect(defaultAudio?.id);
    }
  }, [publicAudioData, privateAudioData]);

  const maxCharLimit = 3500;

  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Grid container gap={3}>
      <Grid item xs={12}>
        <Typography sx={{ ...styles.heading, fontSize: isPage ? 16 : 15 }}>
          Turn your script into professional voiceovers
        </Typography>
        <Box sx={{position: 'relative'}}>
          <TextField
            placeholder="E.g., Text2Create's AI Voiceover transforms your text into captivating, professional audio, ideal for social media content, gaming, and business presentations!"
            size="medium"
            type="text"
            name="prompt"
            multiline
            rows={isExtraSmall ? 4 : 9}
            inputProps={{ maxLength: maxCharLimit }}
            sx={styles.textField}
            value={voiceOverData.text}
            onChange={(e) => handleDataChange('text', e.target.value?.slice(0, maxCharLimit))}
            fullWidth
          />
          <Tooltip title="Enhance prompt with AI" placement="top">
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 4,
                zIndex: 10,
              }}
              disabled={!voiceOverData.text.trim() || isEnhancing}
              onClick={handlePromptEnhance}
            >
              {isEnhancing ? 
                <CircularProgress size={20} /> :
                <NextImage src={'/assets/icons/create-video_default.svg'} alt="" width={24} height={24} />
              }
            </IconButton>
          </Tooltip>
        </Box>
        <Box>
          <Typography
            variant="body2"
            sx={{ color: (theme) => theme.palette.text.placeholder, pl: '2px', pt: '2px' }}
          >
            {voiceOverData?.text?.length || 0}/{maxCharLimit} characters
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12} mt={1.5}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Stack rowGap={2}>
              <Box>
                <Typography sx={{ ...styles.label, fontSize: isPage ? 16 : 14 }}>
                  Enter Name
                </Typography>
                <TextField
                  placeholder="Type name here"
                  size="medium"
                  type="text"
                  name="name"
                  sx={styles.textField}
                  value={voiceOverData.name}
                  onChange={(e) => handleDataChange('name', e.target.value)}
                />
              </Box>
              <Box flex={1}>
                <Typography sx={{ ...styles.label, fontSize: isPage ? 16 : 14 }}>
                  Select audio voice
                </Typography>
                {/* Voice dropdown */}
                <Box onClick={handleOpenPopover} sx={styles.selectAudioBtn}>
                  {!selectedAudio?.name && (
                    <Typography sx={{ color: (theme) => theme.palette.text.placeholder }}>
                      Select Audio
                    </Typography>
                  )}
                  <Typography>{selectedAudio?.name}</Typography>
                  <Iconify icon="mdi:chevron-down" width={16} height={16} />
                </Box>
              </Box>
            </Stack>
          </Grid>
          <Grid
            item
            xs={6}
            sx={{
              alignContent: 'flex-end',
            }}
          >
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={styles.createBtn}
              endIcon={<NextImage src={'/assets/icons/Ai.svg'} alt="" width={24} height={24} />}
              disabled={isLoading || isFetchingRequiredCredits}
              onClick={handleCreateClick}
            >
              Create
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={styles.audioPopover}
      >
        <Box sx={styles.popoverContainer}>
          <Box sx={styles.popoverTabs}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="audio tabs">
              <Tab label="Private Audios" value={AudioTab.Private} />
              <Tab label="Public Audios" value={AudioTab.Public} />
            </Tabs>
          </Box>
          <Box sx={styles.popoverContent}>
            {!!audioList?.length &&
              audioList?.map((audio) => {
                return (
                  <Box
                    key={audio?.id}
                    sx={{
                      ...styles.audioItem,
                      backgroundColor:
                        voiceOverData.audioId === audio?.id
                          ? (theme) => theme.palette.background.ternary
                          : (theme) => theme.palette.background.secondary,
                      '&:hover': {
                        backgroundColor:
                          voiceOverData.audioId === audio?.id
                            ? (theme) => theme.palette.background.ternary
                            : (theme) => theme.palette.background.ternary,
                      },
                    }}
                    onClick={() => {
                      handleAudioTrackSelect(audio);
                      handleAudioSelect(audio?.id);
                      handleClosePopover();
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography>
                        {audio?.name}
                      </Typography>
                    </Stack>
                    {/* Mapping through tags to display values only */}
                    <Stack sx={{ marginTop: 1, gap: 1, flexDirection: 'row', flexWrap: 'wrap' }}>
                      {audio?.modelName && (
                        <Chip
                          label={DbModelToDisplayName[audio.modelName as keyof typeof DbModelToDisplayName] || audio.modelName}
                          variant="outlined"
                          sx={styles.labelChips}
                        />
                      )}
                      {audio.userAudioSample?.flatMap((sample) =>
                        sample?.tags?.map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag.value}
                            variant="outlined"
                            sx={styles.labelChips}
                          />
                        ))
                      )}
                    </Stack>
                  </Box>
                );
              })}
          </Box>
        </Box>
      </Popover>

      <CreditDialog
        isOpen={isCreditDialogOpen}
        onClose={closeCreditDialog}
        isFetchingCreditDetail={isFetchingRequiredCredits}
        onConfirm={handleConfirmGeneration}
        creditDetail={{
          availableCredit: requiredCreditsData?.availableCredits,
          creditNeeded: requiredCreditsData?.requiredCredits,
          creditsInUse: requiredCreditsData?.creditsInUse,
        }}
        isLoading={isLoading}
        onUpgrade={handleUpgrade}
        enoughCreditConfirmBtnTitle={'Generate'}
        enoughCreditModalTitle="Confirm Generation"
        upgradeButtonTitle="Upgrade"
      />

      {isProccessingModalOpen && (
        <VoiceoverProccessingModal
          handleCloseDialog={closeProccessingModal}
          shouldOpenDialog={isProccessingModalOpen}
          textToSpeechId={newGenerationData?.id}
        />
      )}
    </Grid>
  );
};

const styles: Styles = {
  textField: {
    '& .MuiOutlinedInput-root': {
      border: '1px solid',
      borderRadius: '6px',
      borderColor: (theme) => theme.palette.background.ternary,
      backgroundColor: (theme) => theme.palette.background.secondary,
      '&:hover': {
        borderColor: (theme) => theme.palette.background.activeBorder,
      },
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: (theme) => theme.palette.text.placeholder,
    },
  },
  heading: {
    fontSize: 14,
    fontWeight: 500,
    color: (theme) => theme.palette.text.primary,
    marginBottom: '5px',
    marginTop: '5px',
  },
  label: {
    fontSize: 14,
    fontWeight: 400,
    color: (theme) => theme.palette.text.primary,
    marginBottom: '5px',
  },
  createBtn: {
    py: 3,
    fontSize: 16,
    color: (theme) => theme.palette.text.primary,
    boxShadow: 'none',
    alignSelf: 'flex-end',
  },
  selectAudioBtn: {
    cursor: 'pointer',
    border: '1px solid',
    borderColor: (theme) => theme.palette.background.border,
    paddingX: 1,
    height: 52,
    borderRadius: '6px',
    // minWidth: 125,
    // maxWidth: 200,
    '&:hover': {
      borderColor: (theme) => theme.palette.background.activeBorder,
    },
    backgroundColor: (theme) => theme.palette.background.secondary,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    // columnGap: 2,
  },
  popoverContainer: {
    width: '100%',
    paddingX: 2,
    paddingBottom: 2,
    border: '1px solid',
    borderColor: (theme) => theme.palette.background.border,
    backgroundColor: (theme) => theme.palette.background.primary,
  },
  popoverTabs: {
    borderBottom: 1,
    borderColor: 'divider',
    p: 1,
  },
  popoverContent: {
    maxHeight: 200, // Adjust height for scroll
    overflowY: 'auto',
    mt: 2,
    overflowX: 'hidden',
    p: 1,
  },
  audioItem: {
    padding: 1,
    width: '100%',
    cursor: 'pointer',
    my: 0.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  labelChips: {
    backgroundColor: (theme) => theme.palette.background.ternary,
    fontSize: '12px',
    paddingX: '2px',
    height: '20px',
    borderColor: '#b9abab',
    borderRadius: '5px',
    color: '#b9abab'
  },
};
